// src/pages/Preferences.jsx
import { useUser } from "@clerk/clerk-react";
import { useMemo, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { X, Moon, Sun, Monitor, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../@/components/ui/button";
import { ClipLoader } from "react-spinners";
import { useEffect } from "react";
import {
  getBrowserTimezone,
  getUserTimezone,
  setUserTimezone,
} from "../lib/timezone";

// Helper to get timezone offset dynamically (best-effort; handles DST correctly when supported)
const getOffset = (tz) => {
  try {
    const date = new Date();
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    });
    return (
      fmt.formatToParts(date).find((p) => p.type === "timeZoneName")?.value ||
      ""
    );
  } catch {
    return "";
  }
};

const getTimezones = () => {
  try {
    // `supportedValuesOf` isn't available in some older browsers.
    return Intl.supportedValuesOf("timeZone").map((tz) => ({
      value: tz,
      label: `${tz.replace(/_/g, " ")} (${getOffset(tz)})`,
    }));
  } catch {
    return [
      { value: "Africa/Nairobi", label: "Africa/Nairobi (GMT+3)" },
      { value: "UTC", label: "UTC (GMT+0)" },
    ];
  }
};

const getLanguages = () => {
  const commonLanguageCodes = [
    "en",
    "sw",
    "fr",
    "es",
    "pt",
    "de",
    "it",
    "ar",
    "zh",
    "hi",
    "ja",
    "ko",
    "ru",
    "nl",
    "pl",
    "tr",
    "vi",
    "th",
    "id",
    "ms",
  ];

  try {
    const displayNames = new Intl.DisplayNames(["en"], { type: "language" });
    return commonLanguageCodes
      .map((code) => ({
        value: code,
        label: displayNames.of(code) || code,
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  } catch {
    return [
      { value: "en", label: "English" },
      { value: "sw", label: "Swahili" },
      { value: "fr", label: "French" },
      { value: "es", label: "Spanish" },
    ];
  }
};

export default function Preferences({ isOpen, onClose }) {
  const { user } = useUser();
  const { themePreference, setTheme } = useTheme();
  // Get user's timezone: localStorage (if manually set) > browser timezone
  const defaultTimezone = useMemo(() => getUserTimezone(), []);
  const [selectedTheme, setSelectedTheme] = useState(
    themePreference || "system",
  );
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [selectedTimezone, setSelectedTimezone] = useState(defaultTimezone);
  const [isSaving, setIsSaving] = useState(false);

  const timezones = useMemo(() => getTimezones(), []);
  const languages = useMemo(() => getLanguages(), []);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTheme(themePreference || "system");
    const savedLanguage = user?.unsafeMetadata?.language || "en";
    // Priority: user metadata > localStorage > browser timezone
    const savedTimezone = user?.unsafeMetadata?.timezone || getUserTimezone();
    setSelectedLanguage(String(savedLanguage || "en"));
    setSelectedTimezone(String(savedTimezone || defaultTimezone));
  }, [isOpen, themePreference, user, defaultTimezone]);

  const savePreferences = async () => {
    setIsSaving(true);
    try {
      if (!user) {
        toast.error("User not loaded — please try again.");
        return;
      }
      await user.update({
        unsafeMetadata: {
          ...(user?.unsafeMetadata || {}),
          theme: selectedTheme,
          language: selectedLanguage,
          timezone: selectedTimezone,
        },
      });

      // Save timezone to localStorage for use in other parts of the app
      setUserTimezone(selectedTimezone);

      // Persist + apply via ThemeContext (supports "system")
      setTheme(selectedTheme);
      toast.success("Preferences saved successfully.");
      onClose();
    } catch (error) {
      toast.error("Failed to save preferences.");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const themeOptions = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/25 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-(--surface-base) border border-(--neutral-300) rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-(--neutral-300)">
          <h2 className="text-xl font-semibold text-(--neutral-950)">
            Preferences
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-(--neutral-100) rounded-lg transition-colors text-(--neutral-600) hover:text-(--neutral-900)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <label className="block font-semibold text-(--neutral-950)">
              Appearance
            </label>

            {/* Theme Options as Cards */}
            <div className="grid grid-cols-3 gap-3">
              {themeOptions.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setSelectedTheme(value)}
                  className={`
                    relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all
                    ${
                      selectedTheme === value
                        ? "border-(--color-primary)"
                        : "border-(--neutral-200) hover:border-(--color-accent) hover:text-(--color-accent)"
                    }
                  `}
                >
                  {selectedTheme === value && (
                    <div className="absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-(--neutral-900)" />
                    </div>
                  )}
                  <Icon
                    className={`w-5 h-5 ${selectedTheme === value ? "text-(--color-primary)" : "text-(--neutral-600)"}`}
                  />
                  <span
                    className={`text-sm font-medium ${selectedTheme === value ? "text-(--color-primary)" : "text-(--neutral-700)"}`}
                  >
                    {label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Language Selection */}
          <div className="flex flex-col gap-2">
            <label className="block font-semibold text-(--neutral-950)">
              Language
            </label>
            <Select
              value={selectedLanguage}
              onValueChange={setSelectedLanguage}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a language" />
              </SelectTrigger>
              <SelectContent>
                {languages.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Timezone Selection */}
          <div className="flex flex-col gap-2">
            <label className="block font-semibold text-(--neutral-950)">
              Timezone
            </label>
            <Select
              value={selectedTimezone}
              onValueChange={setSelectedTimezone}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a timezone" />
              </SelectTrigger>
              <SelectContent>
                {timezones.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 bg-(--neutral-50) border-(--neutral-300)">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
            className=""
          >
            Cancel
          </Button>
          <Button onClick={savePreferences} disabled={isSaving} className="">
            {isSaving ? (
              <span className="flex items-center gap-2">
                <ClipLoader size={16} color="#fff" />
              </span>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
